(() => {
  const initializeGallery = (gallery) => {
    if (gallery.dataset.galleryReady === 'true') return;
    const allSlides = Array.from(gallery.querySelectorAll('[data-gallery-slide]'));
    const slides = allSlides.filter((slide) => slide.querySelector('img, figure'));
    allSlides.filter((slide) => !slides.includes(slide)).forEach((slide) => { slide.hidden = true; });
    gallery.dataset.galleryReady = 'true';
    gallery.hidden = slides.length === 0;
    if (!slides.length) return;
    const controlGroups = Array.from(gallery.querySelectorAll('[data-gallery-controls]'));
    const position = gallery.querySelector('[data-gallery-position]');
    const roleButtons = Array.from(gallery.querySelectorAll('button[data-gallery-index]'));
    const colorButtons = Array.from(gallery.querySelectorAll('button[data-gallery-color]'));
    const viewport = gallery.querySelector('[data-gallery-viewport]') || gallery;
    const roles = ['front', 'angle', 'open', 'mount', 'pack'];
    let color = gallery.dataset.galleryColor || '';
    let activeRole = roles[0];
    let activeSlides = [];
    let pointerStart = null;
    const refresh = () => {
      activeSlides = roles.map((role) => {
        const matching = slides.filter((slide) => slide.dataset.galleryRole === role);
        if (role !== 'front') return matching[0];
        return matching.find((slide) => !slide.dataset.galleryColor || slide.dataset.galleryColor === color);
      }).filter(Boolean);
      if (!activeSlides.some((slide) => slide.dataset.galleryRole === activeRole)) activeRole = activeSlides[0]?.dataset.galleryRole;
      slides.forEach((slide) => {
        slide.hidden = !activeSlides.includes(slide) || slide.dataset.galleryRole !== activeRole;
      });
      const activeIndex = activeSlides.findIndex((slide) => slide.dataset.galleryRole === activeRole);
      if (position) {
        const template = gallery.dataset.positionTemplate || '%current% / %total%';
        position.textContent = template.replace('%current%', String(activeIndex + 1)).replace('%total%', String(activeSlides.length));
      }
      gallery.hidden = activeSlides.length === 0;
      roleButtons.forEach((button) => {
        const role = roles[Number(button.dataset.galleryIndex)];
        const exists = activeSlides.some((slide) => slide.dataset.galleryRole === role);
        button.hidden = !exists;
        button.setAttribute('aria-pressed', String(role === activeRole));
      });
      colorButtons.forEach((button) => {
        button.hidden = !slides.some((slide) => slide.dataset.galleryRole === 'front' && slide.dataset.galleryColor === button.dataset.galleryColor);
        button.setAttribute('aria-pressed', String(button.dataset.galleryColor === color));
      });
      controlGroups.forEach((group) => {
        const focusedColorButton = colorButtons.find((button) => button === document.activeElement && group.contains(button));
        const hasNavigation = group.querySelector('[data-gallery-prev], [data-gallery-next], [data-gallery-index]');
        const availableColors = colorButtons.filter((button) => group.contains(button) && !button.hidden);
        group.hidden = !(hasNavigation && activeSlides.length > 1) && (activeRole !== 'front' || availableColors.length <= 1);
        if (group.hidden && focusedColorButton) {
          const activeViewButton = roleButtons.find((button) => !button.hidden && roles[Number(button.dataset.galleryIndex)] === activeRole);
          activeViewButton?.focus({ preventScroll: true });
        }
      });
    };
    const move = (delta) => {
      if (activeSlides.length < 2) return;
      const index = activeSlides.findIndex((slide) => slide.dataset.galleryRole === activeRole);
      activeRole = activeSlides[(index + delta + activeSlides.length) % activeSlides.length].dataset.galleryRole;
      refresh();
    };
    gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', () => move(-1));
    gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => move(1));
    roleButtons.forEach((button) => button.addEventListener('click', () => {
      activeRole = roles[Number(button.dataset.galleryIndex)];
      refresh();
    }));
    colorButtons.forEach((button) => button.addEventListener('click', () => {
      const requestedColor = button.dataset.galleryColor;
      if (!slides.some((slide) => slide.dataset.galleryRole === 'front' && slide.dataset.galleryColor === requestedColor)) return;
      color = requestedColor;
      activeRole = 'front';
      refresh();
    }));
    gallery.addEventListener('keydown', (event) => {
      if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.target.closest('input,select,textarea,a')) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        move(event.key === 'ArrowLeft' ? -1 : 1);
      } else if (event.key === 'Home' || event.key === 'End') {
        if (!activeSlides.length) return;
        event.preventDefault();
        activeRole = activeSlides[event.key === 'Home' ? 0 : activeSlides.length - 1].dataset.galleryRole;
        refresh();
      }
    });
    viewport.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'touch' || !event.isPrimary || event.target.closest('a,button,input,select,textarea')) return;
      pointerStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
    });
    viewport.addEventListener('pointerup', (event) => {
      if (!pointerStart || event.pointerId !== pointerStart.id) return;
      const dx = event.clientX - pointerStart.x;
      const dy = event.clientY - pointerStart.y;
      pointerStart = null;
      if (Math.abs(dx) >= 50 && Math.abs(dx) > Math.abs(dy) * 1.3) move(dx < 0 ? 1 : -1);
    });
    viewport.addEventListener('pointercancel', () => { pointerStart = null; });
    refresh();
  };
  const initializeCommerce = (panel) => {
    if (panel.dataset.commerceReady === 'true') return;
    panel.dataset.commerceReady = 'true';
    const select = panel.querySelector('[data-variant-select]');
    const addButton = panel.querySelector('[data-add-to-cart]');
    const warning = panel.querySelector('[data-apply-warning]');
    const acceleratedCheckout = panel.querySelector('[data-accelerated-checkout]');
    if (!select || !addButton) return;
    const checkSelection = () => {
      const changed = select.value !== select.dataset.currentVariant;
      addButton.disabled = changed || addButton.dataset.quantityBlocked === 'true';
      if (acceleratedCheckout) {
        acceleratedCheckout.hidden = changed;
        acceleratedCheckout.inert = changed;
      }
      if (warning) warning.hidden = !changed;
    };
    select.addEventListener('input', checkSelection);
    select.addEventListener('change', checkSelection);
    panel.querySelector('.aq-product-form')?.addEventListener('submit', (event) => {
      if (select.value === select.dataset.currentVariant) return;
      event.preventDefault();
      checkSelection();
      select.focus();
    });
    checkSelection();
  };
  const initializeProductQuantity = (panel) => {
    if (panel.dataset.quantityReady === 'true') return;
    const form = panel.querySelector('.aq-product-form');
    const input = form?.querySelector('[name="quantity"]');
    const addButton = form?.querySelector('[data-add-to-cart]');
    const message = form?.querySelector('[data-quantity-error]');
    if (!form || !input || !addButton) return;
    panel.dataset.quantityReady = 'true';
    const limit = Number(panel.dataset.quantityLimit);
    const inCart = Number(panel.dataset.quantityInCart);
    const select = panel.querySelector('[data-variant-select]');
    const check = () => {
      const quantity = input.valueAsNumber;
      const valid = Number.isInteger(quantity) && input.checkValidity() && quantity + inCart <= limit;
      addButton.dataset.quantityBlocked = String(!valid);
      addButton.disabled = !valid || Boolean(select && select.value !== select.dataset.currentVariant);
      input.setAttribute('aria-invalid', String(!valid));
      if (message) message.hidden = valid;
      return valid;
    };
    input.addEventListener('input', check);
    input.addEventListener('change', check);
    form.addEventListener('submit', (event) => {
      if (check()) return;
      event.preventDefault();
      input.focus();
      input.reportValidity();
    });
    check();
  };
  const initializeCartQuantity = (form) => {
    if (form.dataset.quantityReady === 'true') return;
    const inputs = Array.from(form.querySelectorAll('[data-quantity-line]'));
    const checkout = form.querySelector('[data-quantity-checkout]');
    const message = form.querySelector('[data-quantity-cart-error]');
    const updateMessage = form.querySelector('[data-quantity-cart-update]');
    if (!inputs.length || !checkout) return;
    form.dataset.quantityReady = 'true';
    const limit = Number(form.dataset.quantityLimit);
    const check = () => {
      const validInputs = inputs.every((input) => {
        const quantity = input.valueAsNumber;
        const minimum = Number(input.dataset.quantityMinimum || 1);
        const increment = Number(input.dataset.quantityIncrement || 1);
        const ruleValid = quantity === 0 || (quantity >= minimum && (quantity - minimum) % increment === 0);
        return Number.isInteger(quantity) && input.checkValidity() && ruleValid;
      });
      const total = inputs.reduce((sum, input) => sum + input.valueAsNumber, 0);
      const valid = validInputs && total <= limit;
      const dirty = inputs.some((input) => input.valueAsNumber !== Number(input.dataset.originalQuantity));
      checkout.disabled = !valid || dirty;
      if (message) message.hidden = valid;
      if (updateMessage) updateMessage.hidden = !valid || !dirty;
      inputs.forEach((input) => input.setAttribute('aria-invalid', String(!valid)));
      return { valid, dirty };
    };
    inputs.forEach((input) => {
      input.addEventListener('input', check);
      input.addEventListener('change', check);
    });
    form.addEventListener('submit', (event) => {
      const state = check();
      if (state.valid && (event.submitter?.name !== 'checkout' || !state.dirty)) return;
      event.preventDefault();
      if (!state.valid) inputs.find((input) => !input.checkValidity())?.reportValidity();
      (state.dirty && state.valid ? form.querySelector('[name="update"]') : inputs[0])?.focus();
    });
    check();
  };
  const initialize = (root = document) => {
    root.querySelectorAll('[data-gallery]').forEach(initializeGallery);
    root.querySelectorAll('[data-quantity-product]').forEach(initializeProductQuantity);
    root.querySelectorAll('[data-quantity-cart]').forEach(initializeCartQuantity);
    root.querySelectorAll('[data-commerce-panel]').forEach(initializeCommerce);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('[data-quantity-product] [name="quantity"], [data-quantity-line]').forEach((input) => input.dispatchEvent(new Event('change')));
    document.querySelectorAll('[data-variant-select]').forEach((select) => select.dispatchEvent(new Event('change')));
  });
})();
